import React, { useState } from "react";
import { motion } from "framer-motion";
import { Settings, BarChart, Search, Shield, X } from "lucide-react";
import serviceImage1 from "../../../../assets/Service1.jpg";
import serviceImage2 from "../../../../assets/Service2.jpg";
import serviceImage3 from "../../../../assets/Service3.jpg";
import serviceImage4 from "../../../../assets/Service4.jpg";
import { useTheme } from "../../../../context/ThemeContext";

// Define the services with image URLs and bullet points
const services = [
  {
    title: "Drilling Optimization",
    description:
      "Optimize drilling operations with AI-powered predictive analytics to enhance safety and reduce costs. Improve manufacturing...",
    fullDescription:
      "Optimize drilling operations with AI-powered predictive analytics to enhance safety and reduce costs. This involves analyzing data from various sources to predict potential issues before they occur, allowing for proactive adjustments to be made. By leveraging machine learning algorithms, drilling operations can become more efficient, resulting in reduced downtime and operational costs. In manufacturing, AI-driven solutions streamline production workflows, optimize resource usage, and reduce waste, improving overall efficiency. The use of predictive analytics can enhance product quality, minimize defects, and ensure the timely production of goods, benefiting both drilling and manufacturing sectors by increasing productivity, safety, and cost-effectiveness.",
    icon: <Settings className="font-bold text-5xl" />,
    image: serviceImage1, // Example image URL
    bulletPoints: [
      "Analyze data to predict potential issues",
      "Proactive adjustments to improve operations",
      "Enhanced product quality and efficiency",
    ],
  },
  {
    title: "Reservoir Management",
    description:
      "Monitor drilling parameters in real-time with advanced tools, ensuring seamless operations. Enhance reservoir performance through...",
    fullDescription:
      " Monitor drilling parameters in real-time with advanced tools, ensuring seamless operations and continuous data collection. This allows for quick identification of potential issues, maintaining the overall efficiency of drilling operations. With the integration of IoT sensors, data flows continuously, enabling real-time decision-making. In reservoir management, AI-powered models help predict and optimize production rates, ensuring efficient resource extraction. These models also analyze reservoir data to understand behavior patterns and improve recovery rates. In addition to optimizing drilling performance, AI-driven insights into reservoir conditions lead to better management and improved long-term resource extraction strategies across both drilling and reservoir operations.",
    icon: <BarChart className="font-bold text-5xl" />,
    image: serviceImage2, // Example image URL
    bulletPoints: [
      "Monitor real-time drilling parameters",
      "IoT sensors for continuous data flow",
      "AI models to optimize production rates",
    ],
  },
  {
    title: "Problem Diagnosis",
    description:
      "Identify and resolve complex challenges across Drilling, Reservoir, and Manufacturing with data-driven AI solutions. By using advanced analytics, potential problems can be diagnosed quickly and accurately in all sectors...",
    fullDescription:
      "Identify and resolve complex challenges across Drilling, Reservoir, and Manufacturing operations with AI-powered problem diagnosis. In drilling, AI tools help detect operational issues like equipment malfunctions, wear and tear, or performance inefficiencies. By analyzing drilling data in real-time, AI algorithms can predict problems such as pump failures, downhole tool issues, or pressure and temperature anomalies. In Reservoir management, AI solutions analyze data to identify declining reservoir performance, sudden pressure changes, or other production-related challenges. For Manufacturing, AI can quickly detect problems by analyzing sensor data, identifying production defects, machine breakdowns, or inefficiencies in the manufacturing process. Across all sectors, AI-driven diagnostics enable fast identification and resolution of problems, minimizing downtime and improving operational efficiency in Drilling, Reservoir, and Manufacturing operations.",
    icon: <Search className="font-bold text-5xl" />,
    image: serviceImage3, // Example image URL
    bulletPoints: [
      "Quick and accurate problem diagnosis",
      "Use of advanced analytics for resolution",
      "Minimized disruptions to operations",
    ],
  },
  {
    title: "Safety Enhancement",
    description:
      "Boost safety measures by predicting potential risks and providing actionable safety protocols. This ensures a safer working environment for all personnel involved in the drilling operations...",
    fullDescription:
      "Boost safety measures across drilling, reservoir, and manufacturing operations by predicting potential risks and implementing actionable safety protocols. AI models can predict hazardous conditions such as equipment malfunctions, wellbore instability, or potential health and safety issues. In drilling operations, AI tools can assess risks like blowouts or equipment failure in real-time, providing safety alerts and recommendations. In reservoir management, AI-driven insights can forecast issues related to well integrity, leading to proactive interventions. Similarly, in manufacturing, AI solutions can predict safety hazards related to machinery, worker fatigue, or environmental conditions. By enhancing predictive safety protocols and continuously monitoring operations, AI can significantly improve the safety of workers and operations across all sectors, reducing risks and ensuring compliance with safety regulations.",
    icon: <Shield className="font-bold text-5xl" />,
    image: serviceImage4, // Example image URL
    bulletPoints: [
      "Predict potential risks with AI",
      "Provide actionable safety protocols",
      "Enhance site safety and worker well-being",
    ],
  },
];

const Services = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { isDarkTheme } = useTheme();

  const toggleCard = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const openModal = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    document.body.style.overflow = "auto";
  };

  return (
    <section 
      id="services" 
      className={`py-20 ${
        isDarkTheme 
          ? 'bg-black text-white' 
          : 'bg-white text-black'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-screen-xl">
        {/* Header */}
        <motion.h2
          className={`text-4xl font-bold text-center mb-6 ${
            isDarkTheme 
              ? 'text-amber-400' 
              : 'text-amber-600'
          }`}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          Our Services
        </motion.h2>
        <motion.p
          className={`text-lg text-center mb-16 ${
            isDarkTheme 
              ? 'text-gray-300' 
              : 'text-gray-600'
          }`}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          Empowering petroleum operations with cutting-edge AI solutions for Drilling, Reservoir, and Manufacturing.
        </motion.p>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className={`relative shadow-lg rounded-2xl p-8 flex flex-col items-center text-center transform transition-transform duration-300 hover:scale-105 ${
                isDarkTheme
                  ? 'bg-gray-900 hover:bg-gray-800 text-white'
                  : 'bg-white hover:bg-amber-50 text-black'
              } ${expandedIndex === index ? (isDarkTheme ? 'bg-gray-800' : 'bg-amber-50') : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              style={{
                height: expandedIndex === index ? "auto" : "455px",
                overflow: "hidden",
                paddingBottom: expandedIndex === index ? "3rem" : "3rem",
              }}
            >
              {/* Icon */}
              <div className={`text-5xl font-extrabold mb-6 ${
                isDarkTheme 
                  ? 'text-amber-400 group-hover:text-amber-300' 
                  : 'text-amber-600 group-hover:text-amber-700'
              }`}>
                {service.icon}
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold mb-4 ${
                isDarkTheme 
                  ? 'text-white' 
                  : 'text-gray-800'
              }`}>
                {service.title}
              </h3>

              {/* Description */}
              <p className={`mb-4 ${
                isDarkTheme 
                  ? 'text-gray-300' 
                  : 'text-gray-600'
              }`}>
                {expandedIndex === index ? service.fullDescription : service.description}
              </p>

              {/* Learn More Action */}
              <motion.div
                className={`absolute bottom-5 left-1/2 transform -translate-x-1/2 rounded-full px-4 py-[6px] font-semibold shadow-lg cursor-pointer transition-all ${
                  isDarkTheme
                    ? 'bg-gray-800 text-amber-400 hover:bg-gray-700 hover:text-amber-300'
                    : 'bg-white text-amber-600 hover:bg-amber-600 hover:text-white'
                }`}
                onClick={() => openModal(service)}
              >
                Learn More
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 backdrop-blur-sm">
          <div className={`rounded-lg max-w-3xl w-full h-[90vh] overflow-hidden shadow-lg ${
            isDarkTheme 
              ? 'bg-gray-900 text-white' 
              : 'bg-white text-black'
          }`}>
            <div className={`flex justify-between items-center px-8 py-6 border-b mb-4 ${
              isDarkTheme 
                ? 'border-gray-700' 
                : 'border-gray-200'
            }`}>
              <h3 className={`text-2xl font-bold ${
                isDarkTheme 
                  ? 'text-amber-400' 
                  : 'text-amber-600'
              }`}>
                {selectedService.title}
              </h3>
              <button 
                className={`text-xl ${
                  isDarkTheme 
                    ? 'text-amber-400 hover:text-amber-300' 
                    : 'text-amber-600 hover:text-amber-700'
                }`} 
                onClick={closeModal}
              >
                <X />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className={`px-8 pb-8 overflow-y-auto h-[calc(90vh-4rem)]
              ${
                isDarkTheme ? 
                  "scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent" : 
                  "scrollbar-thin scrollbar-track-transparent"
              }`}>
              <img
                src={selectedService.image}
                alt={selectedService.title}
                className="w-full h-auto rounded-lg mb-6"
              />
              <div className={`${
                isDarkTheme 
                  ? 'text-gray-200' 
                  : 'text-gray-800'
              }`}>
                <h4 className={`text-xl font-semibold mb-4 ${
                  isDarkTheme 
                    ? 'text-amber-400' 
                    : 'text-amber-600'
                }`}>
                  Details
                </h4>
                <p>{selectedService.fullDescription}</p>

                <h4 className={`text-xl font-semibold mt-6 mb-2 ${
                  isDarkTheme 
                    ? 'text-amber-400' 
                    : 'text-amber-600'
                }`}>
                  Key Benefits
                </h4>
                <ul className="list-disc pl-6">
                  {selectedService.bulletPoints.map((point, index) => (
                    <li key={index} className="mb-2">{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;